import axios from 'axios';
import { currencyMap } from '@/utils/currencyMap'; 


interface IpApiResponse {
  country_code2: string; // e.g. "IN"
}


const COUNTRY_STORAGE_KEY = 'user_country_code';
const COUNTRY_STORAGE_CURRENCY = 'user_currency_code';
const EXCHANGE_RATE_PREFIX = 'exchange_rate_';

let countryPromise: Promise<string | null> | null = null;
const exchangeRatePromises = new Map<
  string,
  Promise<{ userCurrencyCode: string; exchangeRate: number } | null>
>();

// Get user's country code with caching
export const getUserCountry = async (): Promise<string | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const cachedCountry = localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (cachedCountry) {
      return cachedCountry;
    }

    if (countryPromise) {
      return countryPromise;
    }

    const accessKey = process.env.NEXT_PUBLIC_IPAPI_KEY; // store in env
    countryPromise = axios
      .get<IpApiResponse>(`https://api.ipgeolocation.io/ipgeo?apiKey=${accessKey}`)
      .then(({ data }: any) => {
        const currencyCode = data?.currency?.code;
        const countryCode = data?.country_code2 || null;

        if (currencyCode) {
          localStorage.setItem(COUNTRY_STORAGE_CURRENCY, currencyCode);
        }

        if (countryCode) {
          localStorage.setItem(COUNTRY_STORAGE_KEY, countryCode);
        }

        return countryCode;
      })
      .catch((error) => {
        console.error('Failed to fetch user country:', error);
        return null;
      })
      .finally(() => {
        countryPromise = null;
      });

    return countryPromise;
  } catch (error) {
    console.error('Failed to fetch user country:', error);
    return null;
  }
};

// Get user's currency and exchange rate
export const getUserExchangeRateWithCountry = async (
  baseCurrency: string = 'US'
): Promise<{ userCurrencyCode: string; exchangeRate: number } | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const cacheKey = `${EXCHANGE_RATE_PREFIX}${baseCurrency.toUpperCase()}`;
    const cachedRate = localStorage.getItem(cacheKey);
    const cachedCurrency = localStorage.getItem(COUNTRY_STORAGE_CURRENCY);

    if (cachedRate && cachedCurrency) {
      return {
        userCurrencyCode: cachedCurrency,
        exchangeRate: Number(cachedRate),
      };
    }

    if (exchangeRatePromises.has(baseCurrency)) {
      return exchangeRatePromises.get(baseCurrency)!;
    }

    const userCountryCode = await getUserCountry();
    if (!userCountryCode) {
      console.warn('Could not detect user country.');
      return null;
    }

    const userCurrencyCode = currencyMap[userCountryCode] || { code: baseCurrency, symbol: '$' };
    const request = axios
      .get<{ rates: Record<string, number> }>(
        `https://api.exchangerate-api.com/v4/latest/${baseCurrency}`
      )
      .then(({ data }) => {
        const rate = data.rates?.[userCurrencyCode.code];
        if (!rate) {
          console.warn(`Rate not found for ${userCurrencyCode.code}`);
          return null;
        }

        localStorage.setItem(COUNTRY_STORAGE_CURRENCY, userCurrencyCode.code);
        localStorage.setItem(cacheKey, String(rate));

        return {
          userCurrencyCode: userCurrencyCode.code,
          exchangeRate: rate,
        };
      })
      .catch((error) => {
        console.error('Error fetching exchange rate:', error);
        return null;
      })
      .finally(() => {
        exchangeRatePromises.delete(baseCurrency);
      });

    exchangeRatePromises.set(baseCurrency, request);
    return request;
  } catch (error) {
    console.error('Error fetching exchange rate:', error);
    return null;
  }
};
